/**
 * Template Utilities
 *
 * Shared template processing functions used by HeadlineFormatter and EquityCardFormatter.
 * Provides security utilities, conditional processing, and math expression evaluation.
 *
 * =============================================================================
 * TEMPLATE SYNTAX SUPPORTED
 * =============================================================================
 *
 * 1. SIMPLE VARIABLE INTERPOLATION (handled by formatters)
 *    {{variableName}}
 *
 * 2. EXISTENCE CHECK
 *    {{#if variableName}}content{{/if}}
 *
 * 3. THRESHOLD CONDITIONAL
 *    {{#if variableName > threshold}}trueValue{{else}}falseValue{{/if}}
 *    Operators: >, >=, <, <=, ==, !=
 *
 * 4. FILTERED CONDITIONAL
 *    {{#if variableName | filterName}}content{{/if}}
 *    Filters: isNumeric, isText, toNumber, uppercase, lowercase, round
 *
 * 5. MATH EXPRESSIONS (left-to-right evaluation)
 *    {{var1 + var2}}
 *    {{var1 - var2 - var3}}
 *    Operators: +, -, *, /
 *
 * 6. SUM ACROSS OWNED CLASSES (portfolio headline only -- see
 *    HeadlineFormatter.processSumBlocks)
 *    {{#sum}}{{ownershipDecimal * classSharePrice}}{{/sum}}
 *    Evaluates the enclosed expression once per share class the shareholder
 *    owns, using that class's own variables, and substitutes one summed
 *    number -- so a portfolio total can be defined AS the sum of what each
 *    class headline's own formula shows, rather than a separately-computed
 *    calculator total that can drift from it.
 *
 * =============================================================================
 */

const TemplateUtils = {
	// ========================================
	// SECURITY CONSTANTS
	// ========================================

	/**
	 * Reserved property names that cannot be used as variables
	 * Prevents prototype pollution attacks
	 */
	RESERVED_NAMES: [
		'__proto__',
		'constructor',
		'prototype',
		'hasOwnProperty',
		'toString',
		'valueOf',
		'toLocaleString',
		'isPrototypeOf',
		'propertyIsEnumerable',
		'__defineGetter__',
		'__defineSetter__',
		'__lookupGetter__',
		'__lookupSetter__',
	],

	/**
	 * Maximum template length to prevent DoS attacks
	 */
	MAX_TEMPLATE_LENGTH: 2000,

	// ========================================
	// FILTERS
	// ========================================
	// Extensible filter registry for use in conditionals and value transformations
	// Usage in conditionals: {{#if user.status | isNumeric}}...{{/if}}
	// Usage in output: {{user.status | toNumber}}

	filters: {
		/**
		 * Check if value is numeric (returns boolean for conditionals)
		 * @param {*} value - Value to check
		 * @returns {boolean} - True if value can be parsed as a number
		 */
		isNumeric: function (value) {
			if (value === undefined || value === null || value === '') return false;
			const num = parseFloat(value);
			return !isNaN(num) && isFinite(num);
		},

		/**
		 * Convert value to number (returns number or undefined)
		 * @param {*} value - Value to convert
		 * @returns {number|undefined} - Numeric value or undefined if not parseable
		 */
		toNumber: function (value) {
			if (value === undefined || value === null || value === '') return undefined;
			const num = parseFloat(value);
			return isNaN(num) ? undefined : num;
		},

		/**
		 * Check if value is NOT numeric (inverse of isNumeric)
		 * @param {*} value - Value to check
		 * @returns {boolean} - True if value cannot be parsed as a number
		 */
		isText: function (value) {
			if (value === undefined || value === null || value === '') return false;
			const num = parseFloat(value);
			return isNaN(num) || !isFinite(num);
		},

		/**
		 * Convert to uppercase
		 * @param {*} value - Value to convert
		 * @returns {string} - Uppercase string
		 */
		uppercase: function (value) {
			if (value === undefined || value === null) return '';
			return String(value).toUpperCase();
		},

		/**
		 * Convert to lowercase
		 * @param {*} value - Value to convert
		 * @returns {string} - Lowercase string
		 */
		lowercase: function (value) {
			if (value === undefined || value === null) return '';
			return String(value).toLowerCase();
		},

		/**
		 * Round a number to the nearest integer
		 * @param {*} value - Value to round
		 * @returns {number|undefined} - Rounded value or undefined
		 */
		round: function (value) {
			if (value === undefined || value === null) return undefined;
			const num = typeof value === 'number' ? value : parseFloat(value);
			return isNaN(num) ? undefined : Math.round(num);
		},
	},

	/**
	 * Apply a filter to a value
	 * @param {*} value - Value to filter
	 * @param {string} filterName - Name of filter to apply
	 * @returns {*} - Filtered value or original if filter not found
	 */
	applyFilter: function (value, filterName) {
		if (!filterName || typeof filterName !== 'string') return value;
		const filter = this.filters[filterName];
		if (typeof filter === 'function') {
			return filter(value);
		}
		console.warn('[TemplateUtils] Unknown filter:', filterName);
		return value;
	},

	/**
	 * Get variable value with support for path-based variables
	 * Checks if varName is a path (e.g., user.status) and resolves via _context if available
	 * Falls back to direct lookup in vars
	 *
	 * @param {Object} vars - Variable map (may include _context for path resolution)
	 * @param {string} varName - Variable name or path
	 * @returns {*} - Resolved value or undefined
	 */
	getVarWithPathSupport: function (vars, varName) {
		if (!varName || !vars) return undefined;

		// Check if it's a path variable with known prefix
		const isPathVar = /^(user|shares|config|aggregate)\./.test(varName);

		if (isPathVar && vars._context) {
			// Resolve path from context
			const parts = varName.split('.');
			const prefix = parts[0];
			const remainingPath = parts.slice(1).join('.');

			const source = vars._context[prefix];
			if (source) {
				// Walk the path
				let current = source;
				for (const part of remainingPath.split('.')) {
					if (current === undefined || current === null) return undefined;
					if (!Object.prototype.hasOwnProperty.call(current, part)) return undefined;
					current = current[part];
				}
				return current;
			}
		}

		// Fall back to direct lookup (may have been pre-resolved by formatter)
		return this.safeGetVar(vars, varName);
	},

	// ========================================
	// SECURITY UTILITIES
	// ========================================

	/**
	 * Safely get a variable value with prototype pollution protection
	 * @param {Object} vars - Variable map
	 * @param {string} varName - Variable name to look up
	 * @returns {*} - Variable value or undefined
	 */
	safeGetVar: function (vars, varName) {
		if (!varName || typeof varName !== 'string') return undefined;
		if (this.RESERVED_NAMES.includes(varName)) {
			console.warn('[TemplateUtils] Blocked reserved variable name:', varName);
			return undefined;
		}
		// Allow simple variable names (alphanumeric + underscore)
		// Also allow path-based variables with known prefixes (user., shares., config., aggregate.)
		const isSimpleVar = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName);
		const isPathVar = /^(user|shares|config|aggregate)\.[a-zA-Z_][a-zA-Z0-9_.]*$/.test(varName);
		if (!isSimpleVar && !isPathVar) {
			console.warn('[TemplateUtils] Invalid variable name:', varName);
			return undefined;
		}
		return Object.prototype.hasOwnProperty.call(vars, varName) ? vars[varName] : undefined;
	},

	/**
	 * Escape HTML entities to prevent XSS attacks
	 * @param {string} str - String to escape
	 * @returns {string} - HTML-escaped string
	 */
	escapeHtml: function (str) {
		if (str === undefined || str === null) return '';
		if (typeof str !== 'string') return String(str);
		return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
	},

	// ========================================
	// CONDITIONAL PROCESSING
	// ========================================

	/**
	 * Evaluate a comparison between a value and threshold
	 * Uses strict whitelist of operators for security
	 * @param {number} value - Value to compare
	 * @param {string} operator - Comparison operator
	 * @param {number} threshold - Threshold to compare against
	 * @returns {boolean} - Comparison result
	 */
	evaluateComparison: function (value, operator, threshold) {
		// Coerce string values to numbers for comparison
		if (typeof value === 'string') value = parseFloat(value);
		if (typeof threshold === 'string') threshold = parseFloat(threshold);
		if (typeof value !== 'number' || isNaN(value)) return false;
		if (typeof threshold !== 'number' || isNaN(threshold)) return false;

		// Strict whitelist - only these operators are allowed
		switch (operator.trim()) {
			case '>':
				return value > threshold;
			case '>=':
				return value >= threshold;
			case '<':
				return value < threshold;
			case '<=':
				return value <= threshold;
			case '==':
				return value === threshold;
			case '!=':
				return value !== threshold;
			default:
				console.warn('[TemplateUtils] Unknown operator blocked:', operator);
				return false;
		}
	},

	/**
	 * Check if a value is "truthy" for template conditionals
	 * @param {*} value - Value to check
	 * @returns {boolean} - Whether the value should be considered truthy
	 */
	isTruthy: function (value) {
		if (value === undefined || value === null) return false;
		if (value === 0) return false;
		if (value === '') return false;
		if (value === false) return false;
		return true;
	},

	/**
	 * Process {{#if ...}}...{{else}}...{{/if}} blocks
	 * Supports: simple existence checks, comparison operators, and filters
	 *
	 * Syntax:
	 *   {{#if varName}}                    - Existence check
	 *   {{#if varName > 5}}                - Comparison
	 *   {{#if varName | filterName}}       - Filter check (result is truthy/falsy)
	 *
	 * @param {string} template - Template string
	 * @param {Object} vars - Variable map
	 * @returns {string} - Template with if blocks resolved
	 */
	processIfBlocks: function (template, vars) {
		const self = this;

		// Regex to match {{#if condition}}...{{/if}} with optional {{else}}
		// Uses non-greedy matching and handles nested content
		const ifBlockRegex = /\{\{#if\s+([^}]+)\}\}([\s\S]*?)\{\{\/if\}\}/g;

		return template.replace(ifBlockRegex, function (_match, condition, content) {
			// Unescape HTML entities in condition (templates may be HTML-escaped in storage)
			condition = condition
				.trim()
				.replace(/&gt;/g, '>')
				.replace(/&lt;/g, '<')
				.replace(/&amp;/g, '&')
				.replace(/&quot;/g, '"')
				.replace(/&#39;/g, "'");

			// Check for {{else}} within the content
			let trueContent = content;
			let falseContent = '';
			const elseIndex = content.indexOf('{{else}}');
			if (elseIndex !== -1) {
				trueContent = content.substring(0, elseIndex);
				falseContent = content.substring(elseIndex + 8); // 8 = '{{else}}'.length
			}

			// Check if condition contains a comparison operator
			// Support both simple vars (varName), path vars (user.status), and quoted strings ("Y")
			const comparisonMatch = condition.match(/^([\w.]+)\s*([<>=!]+)\s*(?:"([^"]*)"|'([^']*)'|([\w.]+))$/);

			// Check if condition contains a filter: {{#if varName | filterName}}
			const filterMatch = condition.match(/^([\w.]+)\s*\|\s*(\w+)$/);

			let conditionResult;
			if (comparisonMatch) {
				const varName = comparisonMatch[1];
				const operator = comparisonMatch[2];
				// Quoted string literal (group 3 or 4) or unquoted variable/number (group 5)
				const quotedValue = comparisonMatch[3] !== undefined ? comparisonMatch[3] : comparisonMatch[4];
				const unquotedRight = comparisonMatch[5];
				const value = self.getVarWithPathSupport(vars, varName);

				if (quotedValue !== undefined) {
					// String comparison: {{#if partner == "Y"}} or {{#if status != 'inactive'}}
					if (operator === '==' || operator === '!=') {
						const lhs = String(value ?? '');
						conditionResult = operator === '==' ? lhs === quotedValue : lhs !== quotedValue;
					} else {
						conditionResult = false;
					}
				} else {
					// Numeric comparison: {{#if tvpi > 1}} or {{#if owned > approved}}
					const rightSide = unquotedRight;
					let threshold = parseFloat(rightSide);
					if (isNaN(threshold)) {
						const rhsValue = self.getVarWithPathSupport(vars, rightSide);
						threshold = typeof rhsValue === 'number' ? rhsValue : parseFloat(rhsValue);
					}

					if (isNaN(threshold)) {
						// Unquoted non-numeric: fall back to string comparison for == and !=
						if (operator === '==' || operator === '!=') {
							const lhs = String(value ?? '');
							const rhs =
								self.getVarWithPathSupport(vars, rightSide) !== undefined ? String(self.getVarWithPathSupport(vars, rightSide)) : rightSide;
							conditionResult = operator === '==' ? lhs === rhs : lhs !== rhs;
						} else {
							conditionResult = false;
						}
					} else {
						conditionResult = self.evaluateComparison(value, operator, threshold);
					}
				}
			} else if (filterMatch) {
				// Filtered condition: {{#if user.status | isNumeric}}
				const varName = filterMatch[1];
				const filterName = filterMatch[2];
				const rawValue = self.getVarWithPathSupport(vars, varName);
				const filteredValue = self.applyFilter(rawValue, filterName);
				conditionResult = self.isTruthy(filteredValue);
			} else {
				// Simple existence check: {{#if varName}}
				const value = self.getVarWithPathSupport(vars, condition);
				conditionResult = self.isTruthy(value);
			}

			// Return appropriate content (will be processed further for nested variables)
			return conditionResult ? trueContent : falseContent;
		});
	},

	// ========================================
	// MATH EXPRESSION PROCESSING
	// ========================================

	/**
	 * Get numeric value from vars, handling string-to-number conversion
	 * @param {Object} vars - Variable map
	 * @param {string} varName - Variable name
	 * @returns {number|null} - Numeric value or null if not found/not numeric
	 */
	getNumericValue: function (vars, varName) {
		const value = this.safeGetVar(vars, varName.trim());
		if (value === undefined || value === null) return null;

		const num = Number(value);
		if (isNaN(num)) return null;

		return num;
	},

	// Match {{expression}} where expression contains operators
	// Pattern: {{varName (operator varName)+}}
	// Examples: {{a + b}}, {{a - b - c}}, {{a * b / c}}
	MATH_EXPRESSION_PATTERN: /\{\{(\w+(?:\s*[+\-*/]\s*\w+)+)\}\}/g,

	/**
	 * Evaluate one math expression's tokens left-to-right against a variable
	 * map. Shared core behind processMathExpressions (formats the result back
	 * into a template string) and sumMathExpressions (used by {{#sum}} blocks
	 * to total a per-class formula across classes).
	 *
	 * @param {string} expression - Expression body, e.g. "a - b - c" (no braces)
	 * @param {Object} vars - Variable map
	 * @returns {number|null} - Computed value, or null if any variable is
	 *   missing/non-numeric, on division by zero, or on a malformed expression
	 */
	_evaluateMathExpression: function (expression, vars) {
		const tokens = expression.split(/\s*([+\-*/])\s*/).filter(function (t) {
			return t.trim();
		});

		if (tokens.length < 3) return null; // Need at least: var op var

		let result = this.getNumericValue(vars, tokens[0]);
		if (result === null) return null;

		for (let i = 1; i < tokens.length; i += 2) {
			const operator = tokens[i];
			const varName = tokens[i + 1];

			if (!varName) break;

			const value = this.getNumericValue(vars, varName);
			if (value === null) return null;

			switch (operator) {
				case '+':
					result += value;
					break;
				case '-':
					result -= value;
					break;
				case '*':
					result *= value;
					break;
				case '/':
					if (value === 0) return null; // Avoid division by zero
					result /= value;
					break;
				default:
					return null; // Unknown operator
			}
		}

		return result;
	},

	/**
	 * Format a number the same way processMathExpressions/sumMathExpressions
	 * render a computed result: integers as whole numbers, decimals to 2 places.
	 */
	formatMathResult: function (n) {
		return Number.isInteger(n) ? n.toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
	},

	/**
	 * Process math expressions in templates (left-to-right evaluation)
	 * Supports: {{var1 + var2}}, {{var1 - var2}}, {{var1 - var2 - var3}}, etc.
	 *
	 * Examples:
	 *   {{eligible - owned}} -> 7 (if eligible=10, owned=3)
	 *   {{eligible - owned - committed}} -> 0 (if eligible=10, owned=3, committed=7)
	 *   {{owned + committed}} -> 10 (if owned=3, committed=7)
	 *
	 * Note: Evaluates left-to-right, no operator precedence or parentheses.
	 * For complex expressions, rewrite: a - (b + c) becomes a - b - c
	 *
	 * Security: Only allows known variable names and basic operators (+, -, *, /)
	 *
	 * @param {string} template - Template with math expressions
	 * @param {Object} vars - Variable map
	 * @returns {string} - Template with math expressions evaluated
	 */
	processMathExpressions: function (template, vars) {
		if (!template || typeof template !== 'string') return '';

		const self = this;

		return template.replace(this.MATH_EXPRESSION_PATTERN, function (_match, expression) {
			try {
				const result = self._evaluateMathExpression(expression, vars);
				if (result === null) return _match; // Unresolvable — leave the raw {{...}} in place
				return self.formatMathResult(result);
			} catch (e) {
				console.warn('[TemplateUtils] Error evaluating math expression:', expression, e);
				return _match; // Return original on error
			}
		});
	},

	// A fragment that's nothing but one bare {{varName}} — no operator, so
	// MATH_EXPRESSION_PATTERN doesn't match it (that pattern requires one).
	BARE_VARIABLE_PATTERN: /^\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}$/,

	/**
	 * Sum every math expression found in a template fragment against a
	 * variable map, returning a raw number rather than a rendered string.
	 * Used by HeadlineFormatter.processSumBlocks to evaluate a {{#sum}}...
	 * {{/sum}} block's enclosed expression once per owned class and add the
	 * results together.
	 *
	 * A fragment that's nothing but one bare variable, e.g. "{{classValue}}"
	 * (no operator to sum over), returns that variable's own value directly —
	 * MATH_EXPRESSION_PATTERN requires an operator, so without this a
	 * {{#sum}}{{classValue}}{{/sum}} block would silently sum to 0.
	 *
	 * A fragment with no math expression, or whose expression can't be
	 * resolved against `vars`, contributes 0 -- same "quietly skip" behavior
	 * as processMathExpressions leaving an unresolvable {{...}} untouched, so
	 * one misconfigured class can't blank out the whole sum.
	 *
	 * @param {string} template - Fragment that may contain a math expression, e.g. "{{ownershipDecimal * classSharePrice}}"
	 * @param {Object} vars - Variable map (typically one class's own var map)
	 * @returns {number} - Sum of every resolvable math expression's value
	 */
	sumMathExpressions: function (template, vars) {
		if (!template || typeof template !== 'string') return 0;

		const bareMatch = template.trim().match(this.BARE_VARIABLE_PATTERN);
		if (bareMatch) {
			const value = this.getNumericValue(vars, bareMatch[1]);
			return value === null ? 0 : value;
		}

		let sum = 0;
		const matches = template.matchAll(this.MATH_EXPRESSION_PATTERN);
		for (const match of matches) {
			try {
				const result = this._evaluateMathExpression(match[1], vars);
				if (result !== null) sum += result;
			} catch (e) {
				console.warn('[TemplateUtils] Error evaluating math expression:', match[1], e);
			}
		}
		return sum;
	},

	/**
	 * Process all conditional expressions in a template
	 * Supports: {{#if var}}...{{/if}}, {{#if var > N}}...{{else}}...{{/if}}
	 * Note: Does NOT process {{var | default: "val"}} - that requires formatter's formatValue
	 *
	 * @param {string} template - Template with conditional expressions
	 * @param {Object} vars - Variable map
	 * @returns {string} - Template with conditionals resolved
	 */
	processConditionals: function (template, vars) {
		if (!template || typeof template !== 'string') return '';

		// Enforce template length limit for DoS prevention
		if (template.length > this.MAX_TEMPLATE_LENGTH) {
			console.warn('[TemplateUtils] Template exceeds max length, truncating');
			template = template.substring(0, this.MAX_TEMPLATE_LENGTH);
		}

		// Process if blocks (may be nested, so process multiple times)
		let previousTemplate;
		let iterations = 0;
		const maxIterations = 10; // Prevent infinite loops

		do {
			previousTemplate = template;
			template = this.processIfBlocks(template, vars);
			iterations++;
		} while (template !== previousTemplate && iterations < maxIterations);

		return template;
	},
};

export { TemplateUtils };
